import { useCallback, useEffect, useRef, useState } from 'react';
import { getSearchCount, SearchCount } from '../api/searchCount';

type CountState = { total: number | null; status: 'pending' | 'ready' | 'error' | 'unavailable' };

function normalize(source: SearchCount | null): CountState {
  if (source && source.total != null && Number.isSafeInteger(source.total) && source.total >= 0 && !source.total_capped) {
    return { total: source.total, status: 'ready' };
  }
  return { total: null, status: source?.count_status === 'error' ? 'error' : source?.count_id ? 'pending' : 'unavailable' };
}

/** Counting never delays the result list or lets an older query overwrite its count. */
export function useSearchCount(source: SearchCount | null) {
  const [result, setResult] = useState<{ source: SearchCount | null; value: CountState } | null>(null);
  const [retryVersion, setRetryVersion] = useState(0);
  const retrySource = useRef<SearchCount | null>(null);
  const value = result?.source === source ? result.value : normalize(source);

  useEffect(() => {
    const initial = normalize(source);
    const retry = retrySource.current === source && source != null;
    retrySource.current = null;
    setResult({ source, value: retry ? { total: null, status: 'pending' } : initial });
    if (!source?.count_id || initial.status === 'ready' || (initial.status === 'error' && !retry)) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    let failures = 0;
    let requested = false;
    const poll = async () => {
      try {
        const retryJob = retry && !requested;
        requested = true;
        const count = await getSearchCount(source.count_id!, controller.signal, retryJob);
        if (controller.signal.aborted) return;
        failures = 0;
        const next = normalize({ ...count, count_id: count.count_id || source.count_id });
        // A malformed ready response must not leave the page polling forever.
        if (count.count_status === 'ready' && next.total == null) next.status = 'error';
        setResult({ source, value: next });
        attempts += 1;
        if (next.status === 'pending') timer = setTimeout(poll, Math.min(1000 + attempts * 1000, 10000));
      } catch {
        if (controller.signal.aborted) return;
        failures += 1;
        // A short disconnect should not discard a healthy server-side count job.
        if (failures < 3) timer = setTimeout(poll, failures * 2000);
        else setResult({ source, value: { total: null, status: 'error' } });
      }
    };
    timer = setTimeout(poll, retry ? 0 : 1000);
    return () => { controller.abort(); clearTimeout(timer); };
  }, [source, retryVersion]);

  const retry = useCallback(() => {
    retrySource.current = source;
    setRetryVersion((version) => version + 1);
  }, [source]);
  return { ...value, retry, canRetry: !!source?.count_id };
}
